import { addToCart } from '~/js/lib/cart'; // eslint-disable-line

/**
 * Pair It With Component
 * Manages dynamic product recommendations based on selected variant
 * Updates recommendations when color or size selection changes
 */
export default (node) => {
  /**
   * Data + elements
   */
  const productHandle = node.dataset.productHandle;
  const sizeSelector = document.querySelector('[data-component="sizeSelect"]');
  const loader = node.querySelector('[data-pair-it-with-loader]');
  // Store available variant options for the currently selected color
  let variantOptions = [];

  /**
   * Functions
   */

  /**
   * Handle add to cart for pair-it-with products
   */
  const handleAddToCart = async (e) => {
    e.preventDefault();

    const variantId = e.currentTarget.getAttribute(
      'button-add-to-cart-pair-it-with'
    );

    const itemToAdd = [
      {
        id: Number(variantId),
        quantity: 1,
      },
    ];
    await addToCart(itemToAdd);

    // Emit event to open cart
    document.dispatchEvent(new CustomEvent('cart:open'));
  };

  /**
   * Show loading spinner overlay
   */
  const showLoader = () => {
    if (loader && !loader.classList.contains('flex')) {
      loader.classList.remove('hidden');
      loader.classList.add('flex');
    }
  };

  /**
   * Hide loading spinner overlay
   */
  const hideLoader = () => {
    if (loader && loader.classList.contains('flex')) {
      loader.classList.add('hidden');
      loader.classList.remove('flex');
    }
  };

  /**
   * Fetch and update pair-it-with products for a specific variant
   * @param {string} currentVariantId - The variant ID to fetch products for
   * @param {HTMLElement} currentContainer - The container element to update
   */
  const fetchPairItWithProducts = async (
    currentVariantId,
    currentContainer
  ) => {
    const fetchUrl = `/products/${productHandle}?variant=${currentVariantId}`;

    showLoader();

    try {
      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const text = await response.text();
      const parsedData = new DOMParser().parseFromString(text, 'text/html');

      const newPairItWithProducts = parsedData.querySelector(
        '[data-pair-it-with-products]'
      );

      if (currentContainer && newPairItWithProducts) {
        const currentProductsGrid = currentContainer.querySelector(
          '[data-pair-it-with-products]'
        );

        if (currentProductsGrid) {
          // Only replace the products grid content, not the title
          currentProductsGrid.innerHTML = newPairItWithProducts.innerHTML;
          attachEventListeners();

          // Show the container if the new content is not hidden
          const fetchedContainer = parsedData.querySelector(
            '[data-component="pairItWith"]'
          );
          if (
            fetchedContainer &&
            !fetchedContainer.classList.contains('hidden')
          ) {
            currentContainer.classList.remove('hidden');
          }
        }
      }

      hideLoader();
    } catch (error) {
      hideLoader();
      if (error?.code === 20) {
        // Code 20 means the call was aborted
        return;
      }
      console.error('[Pair It With] Error fetching products:', error);
      throw error;
    }
  };

  /**
   * Handle color selection change
   * Triggered by colorSelect component via 'pair-it-with:update-content' event
   * Updates variantOptions and fetches products for the selected color/size combination
   */
  const handleUpdateContent = async (e) => {
    const detail = e.detail;

    // Update variant options when color changes
    if (detail?.variantOptions) {
      variantOptions = e.detail.variantOptions;
    }

    // Get active size from event detail or from DOM
    let activeSizeValue = detail?.size;
    if (!activeSizeValue) {
      const activeSize = sizeSelector?.querySelector('.size-selector--active');
      activeSizeValue = activeSize?.dataset.size;
    }

    // Exit if we don't have the required data
    if (variantOptions.length === 0 || !activeSizeValue) return;

    // Find the variant matching the selected size
    const activeSizeVariant = variantOptions.find(
      (variant) => variant.size === activeSizeValue
    );

    // Use found variant or fallback to first available
    const currentVariantId = activeSizeVariant?.id || variantOptions[0].id;

    const currentContainer = document.querySelector(
      '[data-component="pairItWith"]'
    );

    await fetchPairItWithProducts(currentVariantId, currentContainer);
  };

  /**
   * Attach click handlers to add-to-cart buttons
   * Called after each content update to bind events to newly added buttons
   */
  const attachEventListeners = () => {
    const addToCartButton = node.querySelectorAll(
      '[button-add-to-cart-pair-it-with]'
    );
    if (addToCartButton) {
      addToCartButton.forEach((button) => {
        button.addEventListener('click', handleAddToCart);
      });
    }
  };

  /**
   * Initialize variant options from the initially selected color
   * Reads the active color button's variant data on page load
   * This ensures variantOptions are available even if the user changes size first
   */
  const initializeVariantOptions = () => {
    const activeColorButton = document.querySelector(
      '[data-color-select-button].color-select--active'
    );

    if (activeColorButton) {
      const { variants } = activeColorButton.dataset;
      if (variants) {
        // Parse variants string format: "id|size|availability,id|size|availability,..."
        variantOptions = variants.split(',').map((variant) => {
          const options = variant.split('|');
          return {
            id: options[0],
            size: options[1],
            availability: options[2],
          };
        });
      }
    }
  };

  /**
   * Init
   */
  const init = () => {
    initializeVariantOptions();
    attachEventListeners();
    document.addEventListener(
      'pair-it-with:update-content',
      handleUpdateContent
    );
  };

  init();
};
